import { createContext, useContext, useEffect, useState } from "react";

const T = {
  en: {
    nav: { story: "Story", menu: "Menu", picks: "Picks", gallery: "Gallery", reviews: "Reviews", visit: "Visit", order: "Order" },
    hero: {
      kicker: "Beans · Brew · Books — Manama, Bahrain",
      line1: "Every sip",
      line2: "whispers a story",
      tagline: "كل رشفة تحكي قصة",
      taglineArabicFont: true,
      explore: "Explore the Menu",
      order: "Order on Talabat",
      rating: "4.9 · 314 Google reviews",
      scroll: "Scroll to sip",
    },
    marquee: [
      "Signature Spanish Latte",
      "Iced V60 — Yemen",
      "Sipsation Matcha",
      "Pistachio Croissant",
      "Freshly baked waffles",
      "Open till 11 PM",
      "كل رشفة تحكي قصة",
      "4.9 on Google",
    ],
    manifesto: {
      kicker: "The Manifesto",
      chapters: [
        {
          num: "01",
          title: "Slow by design",
          side: "صبر",
          copy: "Nothing here is rushed. Beans are weighed to the gram, water waits at the right degree, and every V60 is poured by hand — because a story told too quickly is a story half-heard.",
        },
        {
          num: "02",
          title: "Beans, brew, books",
          side: "حكايات",
          copy: "Sipsation was never just about caffeine. It's where you laugh louder, talk longer, and feel lighter with every sip. Bring your family. Bring your friends. Bring a book. Stay a while.",
        },
        {
          num: "03",
          title: "Bahrain after dark",
          side: "ليالٍ",
          copy: "Until 11 PM, every night, the lights stay warm on Road 2835. Late conversations, last sips, and the quiet magic of a city winding down — one cup at a time.",
        },
      ],
    },
    menu: {
      kicker: "The Menu",
      titleA: "What's",
      titleB: "brewing",
      side: "القائمة",
      currency: "BHD",
      orderBtn: "Order delivery on Talabat",
      picksNote: "marks a barista's pick · Also available on Keeta",
    },
    combos: {
      kicker: "Picks for you",
      titleA: "Bundled",
      titleB: "chapters",
      sub: "Our most-loved pairings, bundled the way you order them on Talabat.",
      save: "Save",
      was: "was",
      order: "Order on WhatsApp",
      talabat: "Get it on Talabat",
      currency: "BHD",
    },
    gallery: { kicker: "The Gallery", titleA: "Moments in", titleB: "the making" },
    reviews: {
      kicker: "Google Reviews",
      count: "314 reviews",
      titleA: "Loved by",
      titleB: "Bahrain",
      titleC: ", one cup at a time",
    },
    visit: {
      kicker: "Find Us",
      titleA: "Come for the coffee,",
      titleB: "stay for the story",
      hours: "Hours",
      open: "Open now",
      closed: "Closed — back soon",
      location: "Location",
      address1: "Rd 2835, Bld 2770, Block 428",
      address2: "Manama, Kingdom of Bahrain",
      addressAr: "المنامة، مملكة البحرين",
      directions: "Get directions",
    },
    contact: {
      kicker: "Bookings · Meeting Room · Catering",
      titleA: "Write the",
      titleB: "next chapter",
      name: "Your name",
      contactField: "Phone or email",
      email: "Email (for booking confirmation)",
      topics: [
        { value: "booking", label: "Table booking" },
        { value: "meeting", label: "Meeting room reservation" },
        { value: "catering", label: "Catering enquiry" },
        { value: "feedback", label: "Feedback" },
        { value: "general", label: "Something else" },
      ],
      date: "Pick a date",
      time: "Pick a time",
      guests: "Guests",
      guest: (n) => `${n} ${n === 1 ? "guest" : "guests"}`,
      duration: "Duration",
      durations: [
        { value: "1 hour", label: "1 hour" },
        { value: "2 hours", label: "2 hours" },
        { value: "3 hours", label: "3 hours" },
      ],
      purpose: "Meeting purpose (e.g. book club, workshop)",
      message: "Tell us your story...",
      notes: "Anything we should know? (optional)",
      submit: "Send message",
      sending: "Sending...",
      success: "Message received — we'll get back to you soon.",
      needDate: "Please pick a date and time for your reservation.",
      slotTaken: "That time overlaps another meeting-room booking. Please pick a different slot.",
      tooMany: "Too many attempts — please try again in a few minutes.",
      confirmNote: "A confirmation will be emailed to you.",
      availability: { title: "Availability this day", open: "Open", booked: "Booked" },
      error: "Something went wrong. Please try again.",
      sideTitle: "تواصل معنا",
      socials: {
        instagram: "@sipsation.bh · 3.4K followers",
        whatsapp: "Order or say hello",
        talabat: "Delivery across Bahrain",
      },
      room: {
        kicker: "The Meeting Room",
        desc: "A private corner with a smart TV and an on-site printer — made for book clubs, workshops and small teams.",
        price: "7 BHD / hour",
        priceNote: "Fully redeemable as store credit on food & drinks during your session.",
        points: ["Seats up to 8 guests", "Book from 1 to 3 hours", "Smart TV for presentations", "On-site printer", "One group per time slot"],
        tncTitle: "Terms & conditions",
        tnc: [
          "The 7 BHD/hour fee is redeemable as store credit on food & drinks during your session.",
          "Maximum 3 hours and 8 guests per reservation.",
          "One booking per time slot — the room is never double-booked.",
          "Please arrive on time; the room is released for the next guests after your slot.",
          "Reservations are confirmed by email, subject to availability.",
        ],
      },
    },
    footer: {
      taglineMain: "كل رشفة تحكي قصة",
      taglineMainArabic: true,
      taglineSub: "Every sip whispers a story — Manama, Bahrain",
      instagram: "Instagram",
      talabat: "Talabat",
      whatsapp: "WhatsApp",
      rights: (y) => `© ${y} Sipsation Coffee — Beans · Brew · Books`,
      brewed: "Brewed with patience in Manama",
    },
    floating: { whatsapp: "WhatsApp", talabat: "Talabat" },
  },
  ar: {
    nav: { story: "قصتنا", menu: "القائمة", picks: "اختياراتنا", gallery: "المعرض", reviews: "التقييمات", visit: "زورونا", order: "اطلب" },
    hero: {
      kicker: "بُنّ · قهوة · كتب — المنامة، البحرين",
      line1: "كل رشفة",
      line2: "تحكي قصة",
      tagline: "Every sip whispers a story",
      taglineArabicFont: false,
      explore: "استكشف القائمة",
      order: "اطلب من طلبات",
      rating: "4.9 · 314 تقييم على قوقل",
      scroll: "مرّر لترتشف",
    },
    marquee: [
      "سبانيش لاتيه مميز",
      "V60 يمني مثلج",
      "ماتشا سبسيشن",
      "كرواسون فستق",
      "وافل طازج يومياً",
      "مفتوح حتى 11 مساءً",
      "Every sip whispers a story",
      "4.9 على قوقل",
    ],
    manifesto: {
      kicker: "مبادئنا",
      chapters: [
        {
          num: "01",
          title: "التمهّل عن قصد",
          side: "Patience",
          copy: "لا شيء هنا يُستعجل. البُنّ يوزن بالغرام، والماء ينتظر عند الدرجة المناسبة، وكل V60 يُسكب باليد — لأن القصة التي تُروى بسرعة قصةٌ نصف مسموعة.",
        },
        {
          num: "02",
          title: "بُنّ، قهوة، كتب",
          side: "Stories",
          copy: "سبسيشن لم تكن يوماً عن الكافيين فقط. هنا تضحك بصوت أعلى، وتتحدث أطول، وتشعر بخفة مع كل رشفة. أحضر عائلتك، أحضر أصدقاءك، أحضر كتاباً وابقَ قليلاً.",
        },
        {
          num: "03",
          title: "البحرين بعد الغروب",
          side: "Nights",
          copy: "حتى الحادية عشرة مساءً، كل ليلة، تبقى الأنوار دافئة على شارع 2835. أحاديث متأخرة، ورشفات أخيرة، وسحر هادئ لمدينة تهدأ — كوباً بعد كوب.",
        },
      ],
    },
    menu: {
      kicker: "القائمة",
      titleA: "ما الذي",
      titleB: "نُعدّه اليوم",
      side: "The Menu",
      currency: "د.ب",
      orderBtn: "اطلب توصيل من طلبات",
      picksNote: "تعني اختيار الباريستا · متوفر أيضاً على كيتا",
    },
    combos: {
      kicker: "اختياراتنا لك",
      titleA: "توليفات",
      titleB: "محبوبة",
      sub: "أكثر التوليفات طلباً، جمعناها لك كما تطلبها على طلبات.",
      save: "وفّر",
      was: "بدلاً من",
      order: "اطلب عبر واتساب",
      talabat: "اطلبه من طلبات",
      currency: "د.ب",
    },
    gallery: { kicker: "المعرض", titleA: "لحظات في", titleB: "طور التكوين" },
    reviews: {
      kicker: "تقييمات قوقل",
      count: "314 تقييم",
      titleA: "محبوبة في",
      titleB: "البحرين",
      titleC: "، كوباً بعد كوب",
    },
    visit: {
      kicker: "زورونا",
      titleA: "تعال لأجل القهوة،",
      titleB: "وابقَ لأجل الحكاية",
      hours: "أوقات العمل",
      open: "مفتوح الآن",
      closed: "مغلق — نعود قريباً",
      location: "الموقع",
      address1: "شارع 2835، مبنى 2770، مجمع 428",
      address2: "المنامة، مملكة البحرين",
      addressAr: "Manama, Kingdom of Bahrain",
      directions: "الاتجاهات",
    },
    contact: {
      kicker: "الحجوزات · غرفة الاجتماعات · الضيافة",
      titleA: "اكتب",
      titleB: "الفصل القادم",
      name: "الاسم",
      contactField: "رقم الهاتف أو البريد الإلكتروني",
      email: "البريد الإلكتروني (لتأكيد الحجز)",
      topics: [
        { value: "booking", label: "حجز طاولة" },
        { value: "meeting", label: "حجز غرفة اجتماعات" },
        { value: "catering", label: "طلب ضيافة" },
        { value: "feedback", label: "ملاحظات" },
        { value: "general", label: "شيء آخر" },
      ],
      date: "اختر التاريخ",
      time: "اختر الوقت",
      guests: "عدد الضيوف",
      guest: (n) => `${n} ${n === 1 ? "ضيف" : "ضيوف"}`,
      duration: "المدة",
      durations: [
        { value: "1 hour", label: "ساعة واحدة" },
        { value: "2 hours", label: "ساعتان" },
        { value: "3 hours", label: "ثلاث ساعات" },
      ],
      purpose: "غرض الاجتماع (مثال: نادي كتاب، ورشة عمل)",
      message: "أخبرنا قصتك...",
      notes: "هل من شيء نعرفه؟ (اختياري)",
      submit: "أرسل الرسالة",
      sending: "جارٍ الإرسال...",
      success: "وصلتنا رسالتك — سنتواصل معك قريباً.",
      needDate: "الرجاء اختيار التاريخ والوقت للحجز.",
      slotTaken: "هذا الوقت يتعارض مع حجز آخر لغرفة الاجتماعات. الرجاء اختيار وقت مختلف.",
      tooMany: "محاولات كثيرة — الرجاء المحاولة بعد بضع دقائق.",
      confirmNote: "سيصلك تأكيد عبر البريد الإلكتروني.",
      availability: { title: "التوفّر في هذا اليوم", open: "متاح", booked: "محجوز" },
      error: "حدث خطأ ما، حاول مرة أخرى.",
      sideTitle: "Get in touch",
      socials: {
        instagram: "@sipsation.bh · 3.4 ألف متابع",
        whatsapp: "اطلب أو قل مرحباً",
        talabat: "توصيل لجميع مناطق البحرين",
      },
      room: {
        kicker: "غرفة الاجتماعات",
        desc: "ركن خاص مزوّد بشاشة تلفاز ذكية وطابعة — مثالي لنادي الكتاب وورش العمل والفرق الصغيرة.",
        price: "7 د.ب / ساعة",
        priceNote: "قابلة للاسترداد بالكامل كرصيد للمتجر على المأكولات والمشروبات خلال جلستك.",
        points: ["تتّسع حتى 8 ضيوف", "احجز من ساعة إلى 3 ساعات", "شاشة تلفاز ذكية للعروض", "طابعة في الموقع", "مجموعة واحدة لكل موعد"],
        tncTitle: "الشروط والأحكام",
        tnc: [
          "رسوم 7 د.ب/ساعة قابلة للاسترداد كرصيد للمتجر على المأكولات والمشروبات خلال جلستك.",
          "حد أقصى 3 ساعات و8 ضيوف لكل حجز.",
          "حجز واحد لكل موعد — لا يمكن حجز الغرفة مرتين.",
          "الرجاء الحضور في الوقت المحدد؛ تُسلَّم الغرفة للضيوف التاليين بعد انتهاء موعدك.",
          "تُؤكَّد الحجوزات عبر البريد الإلكتروني وفقاً للتوفّر.",
        ],
      },
    },
    footer: {
      taglineMain: "Every sip whispers a story",
      taglineMainArabic: false,
      taglineSub: "كل رشفة تحكي قصة — المنامة، البحرين",
      instagram: "انستغرام",
      talabat: "طلبات",
      whatsapp: "واتساب",
      rights: (y) => `© ${y} سبسيشن كوفي — بُنّ · قهوة · كتب`,
      brewed: "أُعدّت بصبر في المنامة",
    },
    floating: { whatsapp: "واتساب", talabat: "طلبات" },
  },
};

const LangContext = createContext(null);

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState("en");
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  const toggle = () => setLang((l) => (l === "en" ? "ar" : "en"));
  return (
    <LangContext.Provider value={{ lang, isAr: lang === "ar", t: T[lang], toggle }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
